import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import {
  listChannels,
  startTelegramVerification,
  revokeChannel,
  getChannelPreferences,
  setChannelPreference,
  setQuietHours,
  ApiError,
} from '../api';

/**
 * Where nudges reach you (§7.4, Phase 9).
 *
 * The in-app bell is listed alongside Telegram rather than treated as the background
 * default, because it is a channel like any other — that is the whole point of the
 * abstraction. It is the one that cannot be switched off, and the UI says why.
 */

/**
 * A switch for every notification type the app raises.
 *
 * Every type belongs here, because `routeNotification` treats an absent preference as *enabled*:
 * a type missing from this list is one that reaches Telegram and offers no way to stop it. The
 * three team events arrived in Phase 54 and would otherwise have been exactly that — a push for
 * every comment on every task you hold, with no switch on the screen that exists to hold the
 * switches.
 *
 * That was said here before and did not hold. `goal.drift` and `project.invited` were both being
 * delivered with no row on this screen, so remembering was not the mechanism. The mechanism is
 * core's own `NOTIFICATION_TYPES`: a producer cannot invent a type without adding it there and
 * `tsc` refusing, and `test/unit/notificationSwitchesMirrorCore.spec.ts` fails until this list
 * covers every entry. Adding a type without a switch now breaks a build rather than a promise.
 *
 * Exported for that test alone; nothing else reads it.
 */
export const NOTIFICATION_SWITCHES: { type: string; label: string; description: string }[] = [
  {
    type: 'habit.drift',
    label: 'Habit drift',
    description: 'When a habit falls behind its pace, or a limit is reached.',
  },
  {
    type: 'goal.drift',
    label: 'Goal drift',
    description: 'When a goal falls behind its date, goes quiet, or has nothing left to do.',
  },
  {
    type: 'task.reminder',
    label: 'Reminders',
    description: 'Tasks and events with a reminder time.',
  },
  {
    type: 'task.assigned',
    label: 'Work assigned to you',
    description: 'When somebody hands you a task in a shared project.',
  },
  {
    type: 'task.comment',
    label: 'Comments on your tasks',
    description: 'When somebody writes in the thread of a task you are holding.',
  },
  {
    type: 'task.unblocked',
    label: 'Blockers clearing',
    description: 'When a task that was holding up your work is completed.',
  },
  {
    type: 'task.waiting',
    label: 'Chases',
    description: 'When somebody outside the app has gone quiet on something you are waiting for.',
  },
  {
    type: 'task.dormant',
    label: 'Quiet work parking itself',
    description:
      'One notice a week when tasks nobody has worked on in a month park themselves out of ' +
      'your lists. Turning this off stops the notice, not the parking.',
  },
  {
    type: 'project.invited',
    label: 'Invitations',
    description: 'When somebody invites you to a shared project.',
  },
];

function TelegramSetup({ onDone }: { onDone: () => void }) {
  const [started, setStarted] = useState<{ code: string; botUsername: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const begin = useMutation({
    mutationFn: startTelegramVerification,
    onSuccess: (result) => {
      setStarted({ code: result.code, botUsername: result.botUsername });
      setError(null);
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not start that.'),
  });

  if (!started) {
    return (
      <>
        <button onClick={() => begin.mutate()} disabled={begin.isPending}>
          Connect Telegram
        </button>
        {error && <p className="error">{error}</p>}
      </>
    );
  }

  return (
    <div className="notice">
      <strong>Send this to the bot</strong>
      <p className="muted small">
        Open <code>@{started.botUsername}</code> in Telegram and send:
      </p>
      <code className="invite-link">/start {started.code}</code>
      <p className="muted small">
        The code expires in half an hour. Your chat is identified by the message you send, so nobody
        else can connect your account.
      </p>
    </div>
  );
}

export function Channels() {
  const queryClient = useQueryClient();
  const [quietFrom, setQuietFrom] = useState('');
  const [quietUntil, setQuietUntil] = useState('');
  const [quietError, setQuietError] = useState<string | null>(null);

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const prefs = useQuery({ queryKey: ['channel-prefs'], queryFn: getChannelPreferences });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
    void queryClient.invalidateQueries({ queryKey: ['channel-prefs'] });
  };

  const revoke = useMutation({ mutationFn: revokeChannel, onSuccess: refresh });
  const togglePreference = useMutation({ mutationFn: setChannelPreference, onSuccess: refresh });
  const saveQuiet = useMutation({
    mutationFn: setQuietHours,
    onSuccess: () => {
      setQuietError(null);
      refresh();
    },
    onError: (e) => setQuietError(e instanceof ApiError ? e.message : 'Could not save that.'),
  });

  const telegram = channels.data?.find((c) => c.type === 'telegram');
  const quiet = prefs.data?.quietHours;

  /** Absence of a preference row means enabled, matching the server. */
  const isEnabled = (notificationType: string, channelType: string) =>
    prefs.data?.preferences.find(
      (p) => p.notificationType === notificationType && p.channelType === channelType,
    )?.enabled ?? true;

  return (
    <section className="card">
      <h3>Where nudges reach you</h3>

      <QueryState query={channels} />

      <ul className="plain">
        <li className="row between member-row">
          <span>
            <strong>In the app</strong>
            <span className="subtext">The bell. Always on — it is the record, not a copy.</span>
          </span>
          <span className="pill">always</span>
        </li>

        <li className="row between member-row">
          <span>
            <strong>Telegram</strong>
            <span className="subtext">
              {telegram?.verified
                ? `Connected to chat ${telegram.connectedAs}`
                : telegram
                  ? 'Waiting for your code'
                  : 'Not connected'}
            </span>
          </span>
          <span className="row gap">
            {telegram?.verified && <span className="pill ok">connected</span>}
            {telegram && (
              <button className="link danger" onClick={() => revoke.mutate(telegram.id)}>
                Disconnect
              </button>
            )}
          </span>
        </li>
      </ul>

      {!telegram?.verified && <TelegramSetup onDone={refresh} />}

      <h4>What goes to Telegram</h4>
      <ul className="plain">
        {NOTIFICATION_SWITCHES.map((row) => (
          <li key={row.type} className="row between member-row">
            <span>
              {row.label}
              <span className="subtext">{row.description}</span>
            </span>
            <label className="row gap">
              <input
                type="checkbox"
                checked={isEnabled(row.type, 'telegram')}
                disabled={!telegram?.verified || togglePreference.isPending}
                aria-label={`Send ${row.label} to Telegram`}
                onChange={(e) =>
                  togglePreference.mutate({
                    notificationType: row.type,
                    channelType: 'telegram',
                    enabled: e.target.checked,
                  })
                }
              />
            </label>
          </li>
        ))}
      </ul>
      <p className="muted small">
        Switching one off stops the Telegram message only. It still appears in the bell.
      </p>

      <h4>Quiet hours</h4>
      {quiet && (
        <p className="muted small">
          {quiet.from && quiet.until
            ? `No Telegram messages between ${quiet.from} and ${quiet.until}, ${quiet.timezone} time. They arrive when the window ends.`
            : `Not set. Times are read in ${quiet.timezone}.`}
        </p>
      )}
      <div className="row gap">
        <input
          type="time"
          value={quietFrom || (quiet?.from ?? '')}
          aria-label="Quiet from"
          onChange={(e) => setQuietFrom(e.target.value)}
        />
        <span className="muted">to</span>
        <input
          type="time"
          value={quietUntil || (quiet?.until ?? '')}
          aria-label="Quiet until"
          onChange={(e) => setQuietUntil(e.target.value)}
        />
        <button
          onClick={() =>
            saveQuiet.mutate({
              from: quietFrom || quiet?.from || null,
              until: quietUntil || quiet?.until || null,
            })
          }
        >
          Save
        </button>
        <button
          className="link"
          onClick={() => {
            setQuietFrom('');
            setQuietUntil('');
            saveQuiet.mutate({ from: null, until: null });
          }}
        >
          Clear
        </button>
      </div>
      {quietError && <p className="error">{quietError}</p>}
    </section>
  );
}
